import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {Navigate} from "react-router-dom";

import {APPRECIATION_DEFAULTS as appreciationDefaultValues} from "@reva/shared/appreciations";
import {deriveDescriptionKeywordData, detokenizeText} from "@reva/shared/description-keywords";

import AppreciationInputFormField from "../../component/appreciation/AppreciationInputFormField";
import {isAppreciationHidden, useAppreciationsVisible} from "../../component/appreciation/appreciationHooks";
import LoadingIndicator from "../../component/LoadingIndicator";
import {useNavigateWithQuery} from "../../hooks/navigationHooks";
import {useCheckPermission} from "../../hooks/permissionHooks";
import {
    APPRECIATION_KEYS_WITH_VARIANTS,
    selectAllAppreciationVariantTexts,
    selectAppreciationValueOrDefaultCurried,
} from "../../redux/appreciationsUtil";
import {selectAllDocProperties} from "../../redux/objectParametersSlice";
import {selectPersistenceLoading, selectReadOnly} from "../../redux/persistenceSlice";
import {selectLocationInfoDescriptionSpec} from "../../redux/specSlice";
import {PERMISSION_RULE_LOCATION_MICROMACRO_TEXT} from "../../util/permissions";
import {snakeToCamel} from "../../util/stringUtil";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";

// appreciation_location_micro/_macro werden auf Tab Markt/Beschreibung bearbeitet (dort mit
// Marker-Tokens persistiert, siehe DescriptionKeywordUtil.ts) -- hier nur Klartext-Vorschau + Link.
const DESCRIPTION_APPRECIATION_KEYS = new Set(["appreciation_location_micro", "appreciation_location_macro"]);

export function TabAppreciations() {
    const {t} = useTranslation();
    const docProperties = useSelector(selectAllDocProperties);
    const appreciationVariantTexts = useSelector(selectAllAppreciationVariantTexts);
    const persistenceLoading = useSelector(selectPersistenceLoading);
    const readOnly = useSelector(selectReadOnly);
    const navigate = useNavigateWithQuery();
    const appreciationsVisible = useAppreciationsVisible();
    const descriptionSpec = useSelector(selectLocationInfoDescriptionSpec);
    // appreciation_location_micro/_macro fallen ohne gespeicherten Override nicht auf den generischen
    // APPRECIATION_DEFAULTS-Text zurück, sondern (wie auf Tab Markt/Beschreibung) auf den je Bewertung
    // generierten descriptionData.micro_text/macro_text -- deshalb hier dieselbe Selector-Logik.
    const microDescriptionValueOrDefault = useSelector(
        selectAppreciationValueOrDefaultCurried({page: "location", tab: "micro"}),
    );
    const macroDescriptionValueOrDefault = useSelector(
        selectAppreciationValueOrDefaultCurried({page: "location", tab: "macro"}),
    );
    // Ohne diese Permission ist der auto-generierte, marker-basierte Mikro-/Makrolage-Text (Tab Markt/
    // Beschreibung) nicht verfügbar -- appreciation_location_micro/_macro verhalten sich dann wie jede
    // andere Würdigung (Default-Text, normal editierbar), statt Klartext-Vorschau + Link.
    const hasGeneratedDescriptionText = useCheckPermission(PERMISSION_RULE_LOCATION_MICROMACRO_TEXT);

    const descriptionLink = (text) => (
        <span className="cursor-pointer" onClick={() => navigate("/market/description")}>
            {text}
        </span>
    );

    const textEditor = (options) => {
        if (DESCRIPTION_APPRECIATION_KEYS.has(options.rowData.key) && hasGeneratedDescriptionText) {
            return descriptionLink(options.rowData.text);
        }
        const onKeyDown = (e) => {
            // sonst können keine Zeilenumbrüche eingegeben werden
            if (e.key === "Enter") e.stopPropagation();
        };
        return (
            <div onKeyDown={onKeyDown}>
                <AppreciationInputFormField appreciationKey={options.rowData.key} />
            </div>
        );
    };

    // Der ganze Würdigungs-Tab hängt an der Berechtigung (#18607) — Direktaufrufe
    // der URL landen bei den Dokumenten.
    if (!appreciationsVisible) return <Navigate to="/audit/documents" replace />;

    if (persistenceLoading) return <LoadingIndicator loading={persistenceLoading} />;

    // Keywords, die hervorgehoben werden sollen
    const keywords = ["TODO!<br />", "ODER<br />", "Entweder<br />"];

    // Custom Body Template für die Text-Spalte
    const textBodyTemplate = (rowData) => {
        if (DESCRIPTION_APPRECIATION_KEYS.has(rowData.key) && hasGeneratedDescriptionText) {
            return descriptionLink(rowData.text);
        }

        let formattedText = rowData.text;
        if (!formattedText) return "";

        if (/<[^>]+>/.test(formattedText)) return formattedText;

        // Zeilenumbrüche in <br> Tags umwandeln
        formattedText = formattedText.replace(/\n/g, "<br />");

        // Keywords farblich markieren (Case-sensitive)
        keywords.forEach((keyword) => {
            const regex = new RegExp(`\\b${keyword}\\b`, "g");
            formattedText = formattedText.replace(
                regex,
                `<span style="color: #e74c3c; font-weight: bold; border-radius: 3px;">${keyword.toUpperCase()}</span>`,
            );
        });

        return (
            <div
                style={{whiteSpace: "pre-wrap", lineHeight: "1.4"}}
                dangerouslySetInnerHTML={{__html: formattedText}}
            />
        );
    };

    const {categories: descriptionKeywordCategories} = deriveDescriptionKeywordData(descriptionSpec);

    // appreciation_location_micro/_macro: generierter Wert statt APPRECIATION_DEFAULTS-Text, immer detokenisiert
    const resolveEntryText = (key, value) => {
        const rawText =
            key === "appreciation_location_micro"
                ? microDescriptionValueOrDefault
                : key === "appreciation_location_macro"
                  ? macroDescriptionValueOrDefault
                  : (docProperties[key] ??
                    (APPRECIATION_KEYS_WITH_VARIANTS.includes(key) ? appreciationVariantTexts[key] : value.text));
        return DESCRIPTION_APPRECIATION_KEYS.has(key) && rawText
            ? detokenizeText(rawText, descriptionKeywordCategories)
            : rawText;
    };

    const entries = Object.entries(appreciationDefaultValues)
        .filter(([key]) => !key.endsWith("_variant") && !isAppreciationHidden(key))
        .map(([key, value]) => {
            const text = resolveEntryText(key, value);
            // Leerer pdf_title = Würdigung ohne Druckstelle (fachlich offen, #18687); die Spalte „Abschnitt“
            // zeigt dann einen Hinweis statt leer zu bleiben (#19302).
            const title = value?.pdf_title || null;
            if (key === "appreciation_location_overview")
                return {key, routeLabel: t("navigation.address"), route: "/address", title, text};
            const regex =
                /^appreciation_([^_]+)_(points_of_interest|comparative_value|income_value|real_asset_value|[^_]+).*$/;
            const match = key.match(regex);
            if (!match) return {key, title, text};
            const page = match[1];
            const tabLabel = match[2].replace("plot", "groundvalue");
            const tabRoute = `${tabLabel.replace("noise", "emission").replaceAll("_", "-")}`;
            const routeLabel = `${t(`navigation.${page}`)} > ${t(`${page}.category.${snakeToCamel(tabLabel)}`)}`;
            const route = `/${page}/${tabRoute}`;
            return {key, routeLabel, route, title, text};
        })
        .filter((entry) => entry.text);
    if (!entries) {
        return null;
    }

    return (
        <>
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <DataTable
                value={entries}
                emptyMessage={t("value.emptyMessage")}
                removableSort
                stripedRows
                size="small"
                editMode={readOnly ? undefined : "cell"}
            >
                <Column
                    field="routeLabel"
                    body={(rowData) => (
                        <span className={"cursor-pointer"} onClick={() => navigate(rowData.route)}>
                            {rowData.routeLabel}
                        </span>
                    )}
                    style={{
                        minWidth: "6em",
                        width: "15%",
                    }}
                    header={"Tab"}
                    sortable
                />
                <Column
                    field="key"
                    body={(rowData) =>
                        rowData.title ?? (
                            <i style={{color: "var(--text-color-secondary)"}}>
                                {t("audit.tab_appreciations.notPrinted")}
                            </i>
                        )
                    }
                    style={{
                        minWidth: "6em",
                        width: "15%",
                    }}
                    header={"Abschnitt"}
                    sortable
                />
                <Column
                    field="text"
                    style={{
                        minWidth: "16em",
                        width: "70%",
                    }}
                    header={"Text"}
                    body={textBodyTemplate}
                    editor={(options) => textEditor(options)}
                    sortable
                />
            </DataTable>
        </>
    );
}

export default TabAppreciations;
